import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys, useChat } from '@/api/queries';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { StatusChip } from '@/components/StatusChip';
import { PreviewLink } from '@/components/PreviewLink';
import { statusCopy } from './copy/statuses';
import { Composer } from './Composer';
import { DoneDialog } from './DoneDialog';
import { Thread } from './Thread';
import { mergeMessages, useStreamStore } from './stream/stream-store';
import { useChatStream } from './stream/useChatStream';

/**
 * The chat surface - the whole product.
 *
 * One chat per project, so there is no conversation list and no title to
 * manage. What there is instead: a live run view over SSE that resumes cleanly,
 * a composer that always explains itself, and a preview link that tells the
 * truth about whether it is showing the latest change.
 *
 * State ownership follows the rule the project set: TanStack Query holds the
 * chat document (it has a URL), Zustand holds the stream (it has a socket).
 * The two are reconciled here and nowhere else - the fetched history merged
 * with anything the stream has delivered since, keyed on the dense per-
 * visibility `seq`.
 */

export function ChatPage() {
  const params = useParams<{ projectId: string }>();
  const projectId = Number(params.projectId);
  const valid = Number.isFinite(projectId) && projectId > 0;

  const queryClient = useQueryClient();
  const chat = useChat(valid ? projectId : null);

  const reset = useStreamStore((s) => s.reset);
  const seed = useStreamStore((s) => s.seed);
  const storeProjectId = useStreamStore((s) => s.projectId);
  const ledger = useStreamStore((s) => s.ledger);
  const liveMessages = useStreamStore((s) => s.liveMessages);
  const deltas = useStreamStore((s) => s.deltas);
  const streamStatus = useStreamStore((s) => s.status);
  const streamComposer = useStreamStore((s) => s.composer);
  const streamPreview = useStreamStore((s) => s.preview);
  const pendingAsk = useStreamStore((s) => s.pendingAsk);
  const unknown = useStreamStore((s) => s.unknown);
  const connection = useStreamStore((s) => s.connection);
  const refetchToken = useStreamStore((s) => s.refetchToken);

  const [doneOpen, setDoneOpen] = useState(false);

  // A different project means a different stream. Clearing first stops the
  // previous project's ledger from appearing for a frame under the new title.
  useEffect(() => {
    if (valid && storeProjectId !== projectId) reset(projectId);
  }, [valid, projectId, storeProjectId, reset]);

  // Seed the ledger from the chat fetch so a page load mid-run shows the run
  // immediately, before any frame arrives. Every ledger transition is
  // monotonic, so the seed can only be corrected forward.
  useEffect(() => {
    if (!chat.data) return;
    seed({
      activeRun: chat.data.active_run ?? null,
      preview: chat.data.preview ?? null,
      status: chat.data.status,
      composer: chat.data.composer,
      pendingAsk: chat.data.pending_ask ?? null,
    });
  }, [chat.data, seed]);

  // The store asks for a re-read whenever the server tells it something moved
  // that the stream does not carry - done availability, the attachment budget,
  // a stream reset.
  useEffect(() => {
    if (refetchToken === 0 || !valid) return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.chat(projectId) });
  }, [refetchToken, queryClient, projectId, valid]);

  const streamCursor = chat.data?.cursor?.stream_cursor ?? null;
  useChatStream(valid ? projectId : null, streamCursor);

  const messages = useMemo(
    () => mergeMessages(chat.data?.messages ?? [], liveMessages),
    [chat.data?.messages, liveMessages],
  );

  // The stream is fresher than the last fetch for anything it carries.
  const status = streamStatus ?? chat.data?.status;
  const composer = streamComposer ?? chat.data?.composer ?? null;
  const preview = streamPreview ?? chat.data?.preview ?? null;
  const copy = statusCopy(status);

  const contextExhausted = chat.data?.context_exhausted ?? false;
  const doneAvailable = chat.data?.done_available ?? false;

  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  // Follow the run only while the reader is already at the bottom. Yanking
  // someone back down while they are reading an earlier turn is the single
  // most irritating thing a live thread can do.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !stickToBottom.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, ledger.stops, deltas]);

  if (!valid) return <ChatNotice title="That link is not right" body="Pick a product from your list to carry on." />;

  if (chat.isLoading) return <ChatSkeleton />;

  if (chat.isError) {
    const err = chat.error instanceof ApiError ? chat.error : null;
    if (err?.httpStatus === 404) {
      return (
        <ChatNotice
          title="This product is not on your list"
          body="It may have been unassigned. If that is a surprise, get in touch with your account manager."
        />
      );
    }
    return (
      <ChatNotice
        title="We could not load this conversation"
        body={err?.message ?? 'Try again in a moment.'}
        action={
          <Button variant="secondary" onClick={() => void chat.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header: what this is, how it stands, and where to look at it. */}
      <header className="flex-none px-[34px] pb-[22px] pt-[26px] max-[900px]:px-[18px] [@media(max-height:680px)]:px-6 [@media(max-height:680px)]:pb-3 [@media(max-height:680px)]:pt-4">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/" className="eyebrow shrink-0 hover:text-graphite-700 lg:hidden">
            All chats
          </Link>
          <h1 className="max-w-full truncate text-[1.875rem] font-bold leading-[1.06] tracking-[-0.03em] text-graphite-900 max-[900px]:text-[1.6875rem]">
            {chat.data?.project_name ?? 'Your product'}
          </h1>
          <StatusChip status={status} awaitingAnswer={pendingAsk !== null} />

          <span className="ml-auto flex flex-wrap items-center justify-end gap-2.5 max-[1080px]:ml-0 max-[1080px]:mt-3 max-[1080px]:w-full max-[1080px]:justify-start">
            {doneAvailable ? (
              <Button variant="primary" onClick={() => setDoneOpen(true)}>
                Hand over for review
              </Button>
            ) : null}
          </span>
        </div>

        <p className="mt-1.5 text-[0.9375rem] text-graphite-500">{copy.detail}</p>
        <PreviewLink preview={preview} />
      </header>

      {/* The thread. */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="scroll-thin min-h-0 flex-1 overflow-y-auto px-[34px] pb-2 pt-1 max-[900px]:px-[18px] [@media(max-height:680px)]:px-6"
      >
        <div className="measure mx-auto">
          {contextExhausted ? (
            <div className="mb-[18px] flex flex-wrap items-center gap-3.5 rounded-lg bg-butter px-[22px] py-[18px]">
              <p className="min-w-[14rem] flex-1 text-[0.9375rem] text-graphite-500">
                <span className="font-semibold text-graphite-900">This conversation is full.</span> Everything already
                asked for is safe and still on your preview. Get in touch and we will carry on in a fresh one.
              </p>
              <a
                href="mailto:support@example.com"
                className="inline-flex h-[34px] shrink-0 items-center rounded-full bg-bone-raised px-4 text-[0.8125rem] font-semibold text-graphite-900 shadow-[inset_0_0_0_1.5px_var(--rule-strong)] hover:bg-bone"
              >
                Get in touch
              </a>
            </div>
          ) : null}

          <ConnectionNotice phase={connection} />

          {messages.length === 0 && ledger.stops.length === 0 ? (
            <FirstRequest />
          ) : (
            <Thread
              projectId={projectId}
              messages={messages}
              stops={ledger.stops}
              deltas={deltas}
              pendingAsk={pendingAsk}
              unknownEvents={unknown}
            />
          )}
        </div>
      </div>

      {/* The composer. */}
      <div className="flex-none px-[34px] pb-[26px] pt-2.5 max-[900px]:px-[18px] max-[900px]:pb-[18px] [@media(max-height:680px)]:px-6 [@media(max-height:680px)]:pb-3.5">
        <div className="measure mx-auto">
          <Composer projectId={projectId} composer={composer} askPending={pendingAsk !== null} />
        </div>
      </div>

      <DoneDialog
        projectId={projectId}
        open={doneOpen}
        onClose={() => setDoneOpen(false)}
        onDone={() => void chat.refetch()}
      />
    </div>
  );
}

/**
 * The reconnect notice.
 *
 * A dropped stream is not a failure and must not read as one: the server closes
 * mid-run on shutdown as a matter of routine, and the client resumes from its
 * cursor with no gap. So the notice says what is true - we are catching up -
 * and only turns severe when reconnecting has actually stopped.
 */
function ConnectionNotice({ phase }: { phase: string }) {
  if (phase === 'open' || phase === 'closed' || phase === 'connecting') return null;

  const failed = phase === 'failed';
  return (
    <div
      role="status"
      className={cn(
        'mb-[18px] flex items-center gap-3.5 rounded-full px-5 py-3',
        failed ? 'bg-alarm-wash' : 'bg-bone',
      )}
    >
      {failed ? (
        <span aria-hidden className="h-[7px] w-[7px] shrink-0 rounded-full bg-alarm" />
      ) : (
        <span
          aria-hidden
          className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-[1.6px] border-graphite-300 border-r-transparent"
        />
      )}
      <p className="text-[0.90625rem] text-graphite-500">
        {failed ? (
          <>
            <span className="font-semibold text-graphite-900">We have lost the live connection.</span> The work carries
            on on our side — refresh the page and nothing will be missing.
          </>
        ) : (
          'Reconnecting — this happens now and then. The work carries on without you watching.'
        )}
      </p>
    </div>
  );
}

/**
 * The empty thread.
 *
 * The examples are pressable. Someone who has never used this before does not
 * need a description of what a good request looks like — they need one in the
 * box, which they can then edit into their own. The button fills the composer
 * rather than sending, because the first request should still be theirs.
 */
function FirstRequest() {
  const fill = (text: string) => {
    const el = document.getElementById('composer-text') as HTMLTextAreaElement | null;
    if (!el) return;
    // React owns this input, so set the value through the native setter and let
    // the synthetic `input` event carry it into react-hook-form's state.
    const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set;
    setter?.call(el, text);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.focus();
    el.setSelectionRange(text.length, text.length);
  };

  return (
    <div className="mt-2 rounded-lg bg-bone p-7">
      <h2 className="text-[1.375rem] font-bold tracking-[-0.025em] text-graphite-900">
        Tell us what you want changed.
      </h2>
      <p className="mt-1.5 max-w-[34rem] text-graphite-500">
        Plain English is fine. Say what should be different and where — we will ask if anything is unclear.
      </p>
      <div className="mt-4 flex flex-col gap-2.5">
        {[
          'Make the Contact button on the homepage green instead of grey.',
          'Add VAT-inclusive prices to the pricing page.',
          'Put our new opening hours in the footer of every page.',
        ].map((example) => (
          <button
            key={example}
            type="button"
            onClick={() => fill(example)}
            className="rounded-md bg-bone-raised px-5 py-3.5 text-left text-[0.9375rem] text-graphite-500 transition-colors hover:bg-mint hover:text-graphite-900"
          >
            {example}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatSkeleton() {
  return (
    <div className="px-[34px] py-8 max-[900px]:px-[18px]">
      <div className="measure mx-auto">
        <div className="skeleton h-6 w-52 animate-sweep" />
        <div className="mt-7 flex flex-col gap-[22px]">
          {[70, 140, 90].map((height, i) => (
            <div key={i}>
              <div className="skeleton h-3 w-32 animate-sweep" />
              <div className="skeleton mt-2.5 animate-sweep" style={{ height: `${height}px` }} />
            </div>
          ))}
        </div>
        <p className="eyebrow mt-6">Loading this conversation</p>
      </div>
    </div>
  );
}

function ChatNotice({ title, body, action }: { title: string; body: string; action?: React.ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center px-[34px] max-[900px]:px-[18px]">
      <div className="w-full max-w-[34rem] rounded-lg bg-bone p-7">
        <h1 className="text-[1.375rem] font-bold tracking-[-0.025em] text-graphite-900">{title}</h1>
        <p className="mt-1.5 text-graphite-500">{body}</p>
        <div className="mt-5 flex flex-wrap gap-2.5">
          {action}
          <Link
            to="/"
            className="inline-flex h-11 items-center rounded-full bg-bone-raised px-[22px] text-[0.90625rem] font-semibold text-graphite-900 shadow-[inset_0_0_0_1.5px_var(--rule-strong)] hover:bg-bone"
          >
            Back to your chats
          </Link>
        </div>
      </div>
    </div>
  );
}
